import { useEffect, useRef, useState } from 'react';
import { Search, CheckCircle2, UserPlus, Home, Loader2, AlertTriangle, XCircle } from 'lucide-react';
import { apiCall, ApiError, formatCents, dollarsToCents } from '@/lib/api';

type MemberHit = { id: string; name: string };
type CheckInOk = { id: string; ok: true; visitor_name: string | null; warning: string | null };

/**
 * The lobby iPad. Unauthenticated. No PIN, no friction — same model as
 * the paper sign-in clipboard most gyms use.
 *
 *   1. Visitor types their name
 *   2. Pick from the autocomplete (members) — or "I'm a day-pass visitor"
 *   3. Tap big check-in button → green confirmation → reset after 4s
 */
export default function PublicCheckIn() {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<MemberHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [mode, setMode] = useState<'search' | 'day_pass' | 'done' | 'blocked'>('search');
  const [done, setDone] = useState<{ name: string; kind: 'member' | 'day_pass'; warning: string | null } | null>(null);
  const [blocked, setBlocked] = useState<{ name: string; message: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Day pass form state
  const [dpName, setDpName] = useState('');
  const [dpEmail, setDpEmail] = useState('');
  const [dpPriceStr, setDpPriceStr] = useState('15');
  const [submitting, setSubmitting] = useState(false);

  // Debounced member search
  useEffect(() => {
    if (mode !== 'search') return;
    const q = query.trim();
    if (q.length < 2) { setHits([]); return; }
    let active = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await apiCall<{ items: MemberHit[] }>('/v1/check_ins/member-search', {
          query: { q },
        });
        if (active) setHits(r.items);
      } catch {
        if (active) setHits([]);
      } finally {
        if (active) setSearching(false);
      }
    }, 200);
    return () => { active = false; clearTimeout(t); };
  }, [query, mode]);

  async function checkInMember(m: MemberHit) {
    setErr(null);
    setSubmitting(true);
    try {
      const r = await apiCall<CheckInOk>('/v1/check_ins', {
        method: 'POST',
        body: { member_id: m.id, source: 'public' },
      });
      setDone({ name: m.name, kind: 'member', warning: r.warning });
      setMode('done');
      setTimeout(reset, 6000);
    } catch (e) {
      // 412 means the eligibility gate blocked the check-in (expired sub,
      // zero day passes, overdue balance, suspended, etc). Show the
      // friendly reason in a dedicated state rather than a thin error line.
      if (e instanceof ApiError && e.status === 412) {
        const msg = (e.body.message as string | undefined) ?? 'You can\'t check in right now.';
        setBlocked({ name: m.name, message: msg });
        setMode('blocked');
        setTimeout(reset, 10000);
      } else {
        setErr(e instanceof Error ? e.message : 'Check-in failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function submitDayPass(e: React.FormEvent) {
    e.preventDefault();
    if (!dpName.trim()) return;
    setSubmitting(true);
    setErr(null);
    try {
      const dp = await apiCall<{ id: string }>('/v1/day_passes', {
        method: 'POST',
        body: {
          visitor_name: dpName.trim(),
          visitor_email: dpEmail.trim() || null,
          price_cents: dollarsToCents(dpPriceStr),
          method: 'cash',
        },
      });
      // Auto check them in too
      await apiCall('/v1/check_ins', {
        method: 'POST',
        body: { day_pass_id: dp.id, visitor_name: dpName.trim(), source: 'public' },
      });
      setDone({ name: dpName.trim(), kind: 'day_pass', warning: null });
      setMode('done');
      setTimeout(reset, 4000);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Failed');
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setQuery('');
    setHits([]);
    setMode('search');
    setDone(null);
    setBlocked(null);
    setErr(null);
    setDpName('');
    setDpEmail('');
    setDpPriceStr('15');
    setTimeout(() => inputRef.current?.focus(), 100);
  }

  // ── UI ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-indigo-50 to-white">
      <div className="w-full max-w-xl">
        <header className="text-center mb-8">
          <Home className="mx-auto h-10 w-10 text-indigo-600" />
          <h1 className="mt-3 text-3xl font-bold">Welcome to United Wrestling Club</h1>
          <p className="text-gray-500 mt-1">Tap your name to check in.</p>
        </header>

        {/* DONE STATE */}
        {mode === 'done' && done && (
          <div className="bg-green-50 border-2 border-green-300 rounded-2xl p-10 text-center shadow-sm">
            <CheckCircle2 className="mx-auto h-16 w-16 text-green-600" />
            <h2 className="mt-4 text-2xl font-semibold text-green-900">
              You're checked in{done.kind === 'day_pass' ? ' on a day pass' : ''}, {done.name}.
            </h2>
            <p className="text-green-700 mt-2">Have a great workout!</p>
            {done.warning && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-amber-100 border border-amber-300 px-3 py-2 text-sm text-amber-900">
                <AlertTriangle className="h-4 w-4" />
                {done.warning}
              </div>
            )}
            <button
              onClick={reset}
              className="mt-6 text-sm text-green-800 underline hover:text-green-900 block mx-auto"
            >
              Check someone else in
            </button>
          </div>
        )}

        {/* BLOCKED STATE — eligibility gate rejected the check-in */}
        {mode === 'blocked' && blocked && (
          <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-10 text-center shadow-sm">
            <XCircle className="mx-auto h-16 w-16 text-red-600" />
            <h2 className="mt-4 text-2xl font-semibold text-red-900">
              Hi {blocked.name} — please see a staff member.
            </h2>
            <p className="text-red-700 mt-2">{blocked.message}</p>
            <button
              onClick={reset}
              className="mt-6 text-sm text-red-800 underline hover:text-red-900"
            >
              Done
            </button>
          </div>
        )}

        {/* SEARCH MODE */}
        {mode === 'search' && (
          <div className="bg-white border rounded-2xl shadow-sm p-6 space-y-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                ref={inputRef}
                autoFocus
                type="text"
                placeholder="Type your name…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full rounded-xl border bg-white pl-12 pr-4 py-4 text-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {searching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />}
            </div>

            {hits.length > 0 && (
              <ul className="space-y-1">
                {hits.map((m) => (
                  <li key={m.id}>
                    <button
                      onClick={() => void checkInMember(m)}
                      disabled={submitting}
                      className="w-full text-left rounded-xl border bg-white hover:bg-indigo-50 hover:border-indigo-400 transition-colors px-4 py-3 text-lg font-medium disabled:opacity-50"
                    >
                      {m.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {query.trim().length >= 2 && !searching && hits.length === 0 && (
              <p className="text-center text-sm text-gray-500 py-2">
                No members match "{query}".
              </p>
            )}

            <div className="pt-2 border-t">
              <button
                onClick={() => { setMode('day_pass'); setDpName(query); }}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-indigo-300 text-indigo-700 hover:bg-indigo-50 py-4 text-base font-medium"
              >
                <UserPlus className="h-5 w-5" />
                I'm visiting — pay for a day pass
              </button>
            </div>

            {err && <p className="text-sm text-red-600 text-center">{err}</p>}
          </div>
        )}

        {/* DAY PASS MODE */}
        {mode === 'day_pass' && (
          <form onSubmit={submitDayPass} className="bg-white border rounded-2xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold">Day pass</h2>
            <p className="text-sm text-gray-500">Welcome! Quick info, then pay the front desk.</p>
            <input
              type="text"
              placeholder="Your name"
              value={dpName}
              onChange={(e) => setDpName(e.target.value)}
              required
              autoFocus
              className="w-full rounded-xl border px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              type="email"
              placeholder="Email (optional)"
              value={dpEmail}
              onChange={(e) => setDpEmail(e.target.value)}
              className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Day pass price ($)</label>
              <input
                inputMode="decimal"
                value={dpPriceStr}
                onChange={(e) => setDpPriceStr(e.target.value)}
                className="flex-1 rounded-xl border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-500">= {formatCents(dollarsToCents(dpPriceStr))}</span>
            </div>
            {err && <p className="text-sm text-red-600">{err}</p>}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setMode('search')}
                className="flex-1 rounded-xl border py-3 text-sm hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={!dpName.trim() || submitting}
                className="flex-[2] rounded-xl bg-indigo-600 text-white py-3 text-lg font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting ? 'Checking in…' : 'Check in (cash)'}
              </button>
            </div>
          </form>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          membersyncpro
        </p>
      </div>
    </div>
  );
}
